import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box } from '@mui/system';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate } from "react-router-dom";
import { motion } from 'framer-motion';

const FinancialBarChart = ({ onTotalAmountChange }) => {
  const [user, setUser] = useState({});
  const [token, setToken] = useState(() => localStorage.getItem('token') || null);
  const [fetchedData, setFetchedData] = useState([]);
  const [fetchedExpenses, setFetchedExpenses] = useState([]);
  const [fetchedDamageData, setFetchedDamageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dayByDayData, setDayByDayData] = useState([]);
  const [weeklyTotals, setWeeklyTotals] = useState({
    totalSales: 0,
    totalExpenses: 0,
    totalReturns: 0,
    totalActivity: 0
  });
  const [activeDay, setActiveDay] = useState(null);
  const isSmallScreen = useMediaQuery('(max-width: 600px)');
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
    }
  }, [token]);

  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        navigate("/");
        return;
      }

      try {
        const response = await axios.get("https://admin.bizstor.lk/public/api/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          localStorage.removeItem("token");
          navigate("/");
        }
      }
    };

    fetchProfile();
  }, [navigate]);

  useEffect(() => {
    if (user.id) {
      const fetchData = async () => {
        setLoading(true);
        try {
          const response = await axios.get('https://admin.bizstor.lk/public/api/get-data');
          const nisalOrders = response.data.filter(
            (order) => order.usertoken.toLowerCase() === user.id.toString().toLowerCase()
          );
          setFetchedData(nisalOrders);
        } catch (error) {
          console.error('Error fetching data:', error);
        } finally {
          setLoading(false);
        }
      };

      const fetchExpenses = async () => {
        try {
          const response = await axios.get('https://admin.bizstor.lk/public/api/get-expense-data');
          const nisalOrders = response.data.filter(
            (expense) => expense.usertoken.toLowerCase() === user.id.toString().toLowerCase()
          );
          setFetchedExpenses(nisalOrders);
        } catch (error) {
          console.error('Error fetching expenses data:', error);
        }
      };

      const fetchDamageData = async () => {
        try {
          const response = await axios.get('https://admin.bizstor.lk/public/api/get-damage-data');
          const nisalOrders = response.data.filter(
            (damage) => damage.usertoken.toLowerCase() === user.id.toString().toLowerCase() && damage.type === 'money back'
          );
          setFetchedDamageData(nisalOrders);
        } catch (error) {
          console.error('Error fetching damage data:', error);
        }
      };

      fetchData();
      fetchExpenses();
      fetchDamageData();
    }
  }, [user.id]);

  useEffect(() => {
    if (!fetchedData.length && !fetchedExpenses.length && !fetchedDamageData.length) return;

    const now = new Date();
    const currentDay = now.getDay();
    setActiveDay(currentDay === 0 ? 6 : currentDay - 1);
    
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - (currentDay === 0 ? 6 : currentDay - 1));
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    const salesTotals = Array(7).fill(0);
    const expenseTotals = Array(7).fill(0);
    const damageTotals = Array(7).fill(0);
    let totalSales = 0;
    let totalExpenses = 0;
    let totalReturns = 0;

    const getDayIndex = (date) => {
      const dayOfWeek = date.getDay();
      return dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    };

    fetchedData.forEach((order) => {
      const createdAt = new Date(order.invoicedate);
      if (createdAt >= startOfWeek && createdAt <= endOfWeek) {
        const dayIndex = getDayIndex(createdAt);
        let orderTotal = 0;

        order.items.forEach((item) => {
          const itemPrice = Number(item.price);
          const itemTax = item.tax || 0;
          const itemTotal = itemPrice + itemPrice * (itemTax / 100);
          orderTotal += itemTotal;
        });

        const discountPercentage = order.discount || 0;
        const discountAmount = (orderTotal * discountPercentage) / 100;
        orderTotal -= discountAmount;

        salesTotals[dayIndex] += orderTotal;
        totalSales += orderTotal;
      }
    });

    fetchedExpenses.forEach((expense) => {
      const createdAt = new Date(expense.date);
      if (createdAt >= startOfWeek && createdAt <= endOfWeek) {
        const dayIndex = getDayIndex(createdAt);

        expense.items.forEach((item) => {
          const amount = Number(item.amount);
          expenseTotals[dayIndex] += amount;
          totalExpenses += amount;
        });
      }
    });

    fetchedDamageData.forEach((damage) => {
      const createdAt = new Date(damage.invoicedate);
      if (createdAt >= startOfWeek && createdAt <= endOfWeek) {
        const dayIndex = getDayIndex(createdAt);

        damage.items.forEach((item) => {
          const price = Number(item.price);
          damageTotals[dayIndex] += price;
          totalReturns += price;
        });
      }
    });

    const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    const data = daysOfWeek.map((day, index) => ({
      name: day,
      sales: salesTotals[index],
      expenses: expenseTotals[index],
      return: damageTotals[index],
      total: (salesTotals[index] + expenseTotals[index] + damageTotals[index]) || 0
    }));

    const totalActivity = totalSales + totalExpenses + totalReturns;

    setDayByDayData(data);
    setWeeklyTotals({
      totalSales,
      totalExpenses,
      totalReturns,
      totalActivity
    });

    if (onTotalAmountChange) {
      onTotalAmountChange({
        sales: totalSales,
        expenses: totalExpenses,
        returns: totalReturns,
        total: totalActivity
      });
    }
  }, [fetchedData, fetchedExpenses, fetchedDamageData, onTotalAmountChange]);

  // Format numbers with currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'Lkr',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Find the maximum value to scale the bars properly
  const getMaxValue = () => {
    if (!dayByDayData.length) return 0;
    return Math.max(...dayByDayData.map(day => 
      Math.max(day.sales, day.expenses, day.return)
    ));
  };

  const maxValue = getMaxValue();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        padding: '24px',
        borderRadius: '16px',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      {loading ? (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'white',
            zIndex: 9999,
          }}
        >
          <l-trio size="50" speed="1.3" color="red"></l-trio>
        </Box>
      ) : (
        <Box sx={{ width: '100%' }}>
          <Box sx={{ 
            display: 'flex',
            flexDirection: isSmallScreen ? 'column' : 'row',
            gap: 4
          }}>
            {/* Bar Chart Section */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              style={{ 
                flex: '1',
                minHeight: '400px'
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                height: '400px',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                position: 'relative',
                pr: 2,
                pl: 1,
                pb: 6
              }}>

                <Box sx={{ 
                  position: 'absolute',
                  left: 35,
                  right: 0,
                  top: 0,
                  bottom: 40,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  pointerEvents: 'none'
                }}>
                  <Box sx={{ borderTop: '1px dashed #E2E8F0', width: '100%' }} />
                  <Box sx={{ borderTop: '1px dashed #E2E8F0', width: '100%' }} />
                  <Box sx={{ borderTop: '1px dashed #E2E8F0', width: '100%' }} />
                  <Box sx={{ borderTop: '1px dashed #E2E8F0', width: '100%' }} />
                  <Box sx={{ borderTop: '1px dashed #E2E8F0', width: '100%' }} />
                </Box>

                {/* Bar groups */}
                {dayByDayData.map((day, index) => (
                  <motion.div
                    key={day.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.6 }}
                    onClick={() => setActiveDay(index)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      cursor: 'pointer',
                      position: 'relative',
                      zIndex: 1,
                      width: `${100 / 7}%`
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex',
                      width: '100%',
                      height: '310px',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      position: 'relative'
                    }}>
                      {/* Sales bar */}
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: `${(day.sales / maxValue) * 300}px` }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                        style={{
                          width: '8px',
                          backgroundColor: '#9F7AEA',
                          borderRadius: '4px 4px 0 0',
                          marginRight: '4px',
                          position: 'relative'
                        }}
                      >
                        {activeDay === index && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              position: 'absolute', 
                              top: '-20px', 
                              left: '50%', 
                              transform: 'translateX(-50%)',
                              whiteSpace: 'nowrap',
                              color: '#9F7AEA',
                              fontWeight: 'bold'
                            }}
                          >
                            {formatCurrency(day.sales)}
                          </Typography>
                        )}
                      </motion.div>
                      
                      {/* Expenses bar */}
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: `${(day.expenses / maxValue) * 300}px` }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        style={{
                          width: '8px',
                          backgroundColor: '#F56565',
                          borderRadius: '4px 4px 0 0',
                          marginRight: '4px',
                          position: 'relative'
                        }}
                      >
                        {activeDay === index && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              position: 'absolute', 
                              top: '-20px', 
                              left: '50%', 
                              transform: 'translateX(-50%)',
                              whiteSpace: 'nowrap',
                              color: '#F56565',
                              fontWeight: 'bold'
                            }}
                          >
                            {formatCurrency(day.expenses)}
                          </Typography>
                        )}
                      </motion.div>
                      
                      {/* Returns bar */}
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: `${(day.return / maxValue) * 300}px` }}
                        transition={{ duration: 0.8, delay: 0.5 }}
                        style={{
                          width: '8px',
                          backgroundColor: '#FFB300',
                          borderRadius: '4px 4px 0 0',
                          position: 'relative'
                        }}
                      >
                        {activeDay === index && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              position: 'absolute', 
                              top: '-20px', 
                              left: '50%', 
                              transform: 'translateX(-50%)',
                              whiteSpace: 'nowrap',
                              color: '#FFB300',
                              fontWeight: 'bold'
                            }}
                          >
                            {formatCurrency(day.return)}
                          </Typography>
                        )}
                      </motion.div>
                    </Box>
                    
                    {/* X-axis labels */}
                    <Box 
                      sx={{
                        mt: 1,
                        pt: 1,
                        borderTop: '1px solid #E2E8F0',
                        width: '100%',
                        textAlign: 'center'
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: activeDay === index ? 700 : 400,
                          color: activeDay === index ? '#4299E1' : '#718096'
                        }}
                      >
                        {day.name}
                      </Typography>
                    </Box>
                  </motion.div>
                ))}
              </Box>

              {/* Chart Legend */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                mt: 2, 
                gap: 3 
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: '#9F7AEA', mr: 1 }} />
                  <Typography variant="caption" sx={{ color: '#718096' }}>Sales</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: '#F56565', mr: 1 }} />
                  <Typography variant="caption" sx={{ color: '#718096' }}>Expenses</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: '#FFB300', mr: 1 }} />
                  <Typography variant="caption" sx={{ color: '#718096' }}>Returns</Typography>
                </Box>
              </Box>
            </motion.div>

            {/* Weekly Summary Section (replaces Details Section) */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              style={{ 
                flex: '0 0 300px', 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <Box sx={{ 
                p: 3, 
                bgcolor: '#F7FAFC', 
                borderRadius: '16px',
                border: '1px solid #EDF2F7'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 3, 
                    color: '#2D3748',
                    fontWeight: 700,
                    textAlign: 'center'
                  }}
                >
                  Weekly Summary
                </Typography>

                {/* Stats cards for weekly totals */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: 2
                }}>
                  <motion.div
                    initial={{ x: 50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                  >
                    <StatCard 
                      title="Total Sales"
                      value={weeklyTotals.totalSales}
                      color="#9F7AEA"
                      percentage={Math.round((weeklyTotals.totalSales / weeklyTotals.totalActivity) * 100) || 0}
                    />
                  </motion.div>
                  
                  <motion.div
                    initial={{ x: 50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                  >
                    <StatCard 
                      title="Total Expenses"
                      value={weeklyTotals.totalExpenses}
                      color="#F56565"
                      percentage={Math.round((weeklyTotals.totalExpenses / weeklyTotals.totalActivity) * 100) || 0}
                    />
                  </motion.div>
                  
                  <motion.div
                    initial={{ x: 50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.7, duration: 0.5 }}
                  >
                    <StatCard 
                      title="Total Returns"
                      value={weeklyTotals.totalReturns}
                      color="#FFB300"
                      percentage={Math.round((weeklyTotals.totalReturns / weeklyTotals.totalActivity) * 100) || 0}
                    />
                  </motion.div>

                  <Box sx={{ height: 1, bgcolor: '#E2E8F0', my: 1 }} />

                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.9, duration: 0.5 }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#718096' }}>
                        Total Weekly Activity
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#4299E1' }}>
                        {formatCurrency(weeklyTotals.totalActivity)}
                      </Typography>
                    </Box>
                  </motion.div>
                </Box>
              </Box>
            </motion.div>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// Component for stat cards
const StatCard = ({ title, value, color, percentage }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'Lkr',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <Box sx={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '12px',
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      border: '1px solid #EDF2F7'
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: color
          }} />
          <Typography variant="body2" sx={{ color: '#718096' }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ 
          color: color,
          fontWeight: 600,
          bgcolor: `${color}10`,
          px: 1,
          py: 0.5,
          borderRadius: 1
        }}>
          {percentage}%
        </Typography>
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#2D3748' }}>
        {formatCurrency(value)}
      </Typography>
      <Box sx={{ 
        width: '100%', 
        height: 4, 
        bgcolor: '#EDF2F7',
        borderRadius: 2,
        overflow: 'hidden'
      }}>
        <Box sx={{ 
          width: `${percentage}%`, 
          height: '100%', 
          bgcolor: color,
          borderRadius: 2
        }} />
      </Box>
    </Box>
  );
};

export default FinancialBarChart;